import React, { useCallback, useEffect, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { NativeResources } from '@ariksa/inventory-core';
import { AlertResponse } from '@ariksa/notification';
import { AlertLogDetails, NotificationFor } from '@ariksa/notification/api';
import {
  Box,
  Divider,
  Grid,
  GridItem,
  HStack,
  Stack,
  Text,
  useDisclosure,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { each, isEmpty, map, some } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { customTheme } from 'theme';

import {
  CustomTooltip,
  DetailsIdentifier,
  EllipsisText,
  formatTooltip,
  getIcon,
  PlusCountTag,
  renderRiskContextIcon,
} from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { renderCompliance } from 'components/DataDisplay/Utils/renderCompliance';
import { GrayIconButton } from 'components/DataEntry';
import { CustomDayPicker } from 'components/DataEntry/Date';
import {
  ExceptionIcon,
  GraphIcon,
  IssuesIcon,
  NotifyIcon,
  SummaryIcon,
  ThirdPartyIcon,
  TicketIcon,
} from 'components/Icons';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import {
  renderSlaViolation,
  vmPortNames,
} from 'containers/Findings/AlertDrawer/Components/Aggregated/utils';
import { VMOpenPorts } from 'containers/Findings/AlertDrawer/Components/Aggregated/VMOpenPorts';
import { AlertRemediation } from 'containers/Findings/AlertDrawer/Components/Individual/Remediation/AlertRemediation';
import { useAlertStatus } from 'containers/Findings/AlertDrawer/hooks/useAlertStatus';
import { selectAlertDrawer } from 'containers/Findings/AlertDrawer/selectors';
import { actions as alertDrawerActions } from 'containers/Findings/AlertDrawer/slice';
import { AddException } from 'containers/Findings/Alerts/Components/AddException';
import { CreateNotification } from 'containers/Findings/Alerts/Components/CreateNotification';
import { CreateTicket } from 'containers/Findings/Alerts/Components/CreateTicket';
import { selectFindingsOverview } from 'containers/Findings/FindingsOverview/selectors';
import { actions } from 'containers/Findings/FindingsOverview/slice';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { securityGraphRedirectUrl } from 'containers/Visibility/SecurityGraphNext/utils/securityGraphRedirectUrl';
import { formatDate } from 'utils/date';

interface AlertDetailsContentProps {
  alert: AlertResponse;
}

export const AlertDetailsContent = (props: AlertDetailsContentProps) => {
  const { alert } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { compliance } = useSelector(selectFindingsOverview);

  const {
    alertStatus,
    resourceInsight,
    riskContext,
    vmOpenPorts,
  } = useSelector(selectAlertDrawer);
  const { securityGraphAvailability, isDevOrTest } = useSelector(
    selectSharedState,
  );
  const { getAlertStatus } = useAlertStatus();
  const { accountMapping, renderAccountWithVPCAndRegion } = useCloudAccountId();
  const [ticket, setTicket] = useState<AlertLogDetails>({} as AlertLogDetails);
  const [owner, setOwner] = useState<string>('');
  const [showMore, setShowMore] = useState(false);

  const ticketModal = useDisclosure();
  const notificationModal = useDisclosure();
  const exceptionModal = useDisclosure();
  const { getTabIndex } = useMetadataDrawerTabs();
  const [vmHasOpenPorts, setVmHasOpenPorts] = useState<boolean>(false);

  useEffect(() => {
    getAlertStatus(alert.uuid!);
  }, [getAlertStatus, alert]);

  useEffect(() => {
    dispatch(
      actions.getCompliance({
        q: {
          getRuleComplianceStandardRequest: {
            rules: [alert.alert_rule_id ?? ''],
          },
        },
      }),
    );
    dispatch(
      sharedStateActions.getSecurityGraphAvailability({
        q: { resources: [alert?.resource!] },
      }),
    );
    dispatch(
      alertDrawerActions.getRiskContext({
        q: { riskContextRequest: { uuids: [alert.entity_uuid!] } },
      }),
    );
    dispatch(
      alertDrawerActions.getResourceInsight({
        q: {
          insightV2Request: {
            resource_type: alert.resource as NativeResources,
            resource_uuids: [alert.entity_uuid!],
            account_id: [alert.account_id!],
          },
        },
        onSuccess: res => {
          const tagsMapping: Record<string, string> = {};
          each(res?.items?.[0]?.tags, o => (tagsMapping[o?.key] = o?.value));
          setOwner(
            res?.items?.[0]?.owner ??
              tagsMapping['Owner'] ??
              tagsMapping['owner'] ??
              'None',
          );
        },
      }),
    );
    if (
      alert?.resource === NativeResources.Ec2Instance ||
      alert?.resource === NativeResources.GcpComputeEngine ||
      alert?.resource === NativeResources.AzureVirtualMachine
    )
      dispatch(
        alertDrawerActions.getVMOpenPorts({
          q: { resourceId: alert?.entity_uuid! },
          onSuccess: res => {
            setVmHasOpenPorts(some(vmPortNames, o => !!res?.[0]?.[o]?.length));
          },
        }),
      );
  }, [dispatch, alert]);

  useEffect(() => {
    const val = alertStatus.data?.[alert?.uuid!]?.[0];
    if (!!val) setTicket(val);
  }, [alertStatus.data, alert]);

  // open resource metadata drawer
  const { updateActiveResource } = useActiveResourceActions();
  const { onOpenMetadata } = useActiveResourceContext();
  const openMetadataDrawer = (tabIndex = 0) => {
    updateActiveResource({
      uuid: alert.entity_uuid!,
      resourceId: alert.entity_id!,
      resourceType: alert.resource as NativeResources,
      accountId: alert.account_id ?? '',
      cloudProvider: alert?.cloud?.toLowerCase() as CloudProviders,
    });
    onOpenMetadata(tabIndex);
  };

  // open security graph
  const openSecurityGraph = () => {
    const redirect_url = location?.pathname + location?.search;

    const url = securityGraphRedirectUrl({
      alert_id: alert.uuid,
      resource_id: alert.entity_id,
      source_resource_uuid: alert.entity_uuid,
      resource_type: alert.resource as NativeResources,
      account_id: alert.account_id,
      alert_rule_id: alert.alert_rule_id,
      redirect_url: redirect_url,
      search_uuid: alert.search_uuid ?? '',
    });

    navigate(url, { state: alert });
  };

  const handleShowAllAlerts = () => {
    openMetadataDrawer(getTabIndex(alert.resource, MetadataTabs.Alerts));
  };

  const handleAddException = () => {
    exceptionModal.onOpen();
  };

  const updateAlertDueDate = useCallback(
    date => {
      dispatch(
        alertDrawerActions.updateDueDate({
          q: {
            ticketUpdateRequest: {
              client_id: ticket?.client_id!,
              ticket_id: ticket?.ticket_id!,
              field_type: 'due_date',
              field_value: date,
            },
          },
          onSuccess: () => {
            getAlertStatus(alert.uuid!);
          },
        }),
      );
    },
    [alert, dispatch, ticket, getAlertStatus],
  );

  return (
    <Stack spacing={4}>
      <HStack spacing={3} pb={3}>
        <GrayIconButton
          leftIcon={<SummaryIcon />}
          onClick={() => openMetadataDrawer()}
        >
          Details
        </GrayIconButton>
        <GrayIconButton
          leftIcon={<GraphIcon p="1px" />}
          onClick={openSecurityGraph}
          isLoading={securityGraphAvailability.isLoading}
          isDisabled={!securityGraphAvailability.data?.[alert?.resource!]}
        >
          Security Graph
        </GrayIconButton>
        <GrayIconButton
          leftIcon={<ExceptionIcon p="1px" />}
          onClick={handleAddException}
          iconSize={6}
          iconSpacing={1}
        >
          Ignore
        </GrayIconButton>
        <GrayIconButton leftIcon={<IssuesIcon />} onClick={handleShowAllAlerts}>
          Show All Issues
        </GrayIconButton>
        <GrayIconButton
          leftIcon={<NotifyIcon p="1px" />}
          onClick={() => {
            notificationModal.onOpen();
          }}
        >
          Notify User
        </GrayIconButton>
        <GrayIconButton
          leftIcon={<TicketIcon />}
          onClick={() => {
            ticketModal.onOpen();
          }}
          isDisabled={!isEmpty(ticket)}
          isLoading={alertStatus.isLoading}
        >
          Create Ticket
        </GrayIconButton>
      </HStack>
      <Divider />

      <Grid alignItems="start" templateColumns="repeat(3, 1fr)" gap={1}>
        <GridItem colSpan={2}>
          <Stack spacing={4} flex={2} borderRight={'1px solid #eee'} pr={4}>
            <DetailsIdentifier
              label="Issue Description"
              isInline={false}
              value={
                <Stack>
                  <Wrap>
                    <WrapItem>{alert?.message}</WrapItem>
                    {vmHasOpenPorts && (
                      <>
                        <WrapItem>
                          <Box>Virtual Machine has following open ports:</Box>
                        </WrapItem>
                        <VMOpenPorts
                          data={vmOpenPorts.data}
                          isLoading={vmOpenPorts.isLoading}
                          alert={alert}
                        />
                      </>
                    )}
                  </Wrap>
                  {vmHasOpenPorts && (
                    <Text
                      color="primary"
                      cursor="pointer"
                      _hover={{ textDecoration: 'underline' }}
                      onClick={() => {
                        openMetadataDrawer(
                          getTabIndex(alert.resource, MetadataTabs.Network),
                        );
                      }}
                    >
                      Click to view all ingress traffic rules
                    </Text>
                  )}
                </Stack>
              }
              spacing={3}
            />
            {!showMore ? (
              <Stack>
                <DetailsIdentifier
                  label="Why Is This Important"
                  value={
                    <Box
                      fontSize="sm"
                      color="primary"
                      onClick={() => setShowMore(true)}
                      cursor="pointer"
                    >
                      View More
                    </Box>
                  }
                  isInline={false}
                  spacing={3}
                />
              </Stack>
            ) : (
              <Stack spacing={4}>
                <DetailsIdentifier
                  label="Why Is This Important"
                  value={alert?.check_details?.why_important}
                  isInline={false}
                  spacing={3}
                />
                <DetailsIdentifier
                  label="Possible Remediation Options"
                  value={alert?.check_details?.remediation}
                  isInline={false}
                  spacing={3}
                />
                <Box
                  fontSize="sm"
                  color="primary"
                  onClick={() => setShowMore(false)}
                  cursor="pointer"
                >
                  View Less
                </Box>
              </Stack>
            )}
          </Stack>
        </GridItem>
        <GridItem>
          <Box pl={5}>
            <DetailsIdentifier
              label="References"
              spacing={3}
              isInline={false}
              value={
                !!alert?.check_details?.reference_links ? (
                  <Stack>
                    {map(alert?.check_details?.reference_links, o => (
                      <Link to={o} target="_blank">
                        <EllipsisText color="primary">{o}</EllipsisText>
                      </Link>
                    ))}
                  </Stack>
                ) : (
                  'None'
                )
              }
            />
          </Box>
        </GridItem>
      </Grid>
      <Divider />

      <Grid
        templateColumns="repeat(3, 1fr)"
        py={1}
        alignItems={'start'}
        gap={1}
      >
        <GridItem colSpan={2}>
          <Stack spacing={4} flex={2} pr={4} borderRight={'1px solid #eee'}>
            <HStack justifyContent={'space-between'} h={30}>
              <Box fontSize="lg" fontWeight={600}>
                Resource details
              </Box>
              <GrayIconButton
                leftIcon={<ThirdPartyIcon />}
                isDisabled={!isDevOrTest}
                tooltip={
                  'Show resource in ' +
                  accountMapping?.[
                    alert?.account_id!
                  ]?.cloud_type?.toUpperCase() +
                  ' console'
                }
                tooltipStyles={{ tooltip: { w: 32, textAlign: 'center' } }}
                onClick={() =>
                  window.open(
                    resourceInsight?.data?.resource_console_url,
                    '_blank',
                  )
                }
              >
                <Box boxSize={6}>
                  {getIcon(
                    accountMapping?.[alert?.account_id!]?.cloud_type ?? '',
                  )}
                </Box>
              </GrayIconButton>
            </HStack>
            <HStack alignItems={'start'} w={'full'}>
              <Stack flex={1} spacing={3}>
                <DetailsIdentifier
                  label="Name"
                  value={<EllipsisText label={alert.entity_name} />}
                />
                <DetailsIdentifier
                  label="Cloud ID"
                  value={<EllipsisText label={alert.entity_id} />}
                />
                <DetailsIdentifier
                  label="Risk context"
                  isLoading={riskContext.isLoading}
                  value={
                    <Box>
                      <HStack spacing={0.5}>
                        {map(riskContext.data, (o, index) => (
                          <Box key={index + '-risk-context'}>
                            {renderRiskContextIcon(o)}
                          </Box>
                        ))}
                      </HStack>
                    </Box>
                  }
                />
                <DetailsIdentifier
                  label="Owned by"
                  isLoading={resourceInsight.isLoading}
                  value={owner}
                />
              </Stack>
              <Stack flex={1} spacing={3}>
                <DetailsIdentifier
                  label={'Account'}
                  value={renderAccountWithVPCAndRegion(resourceInsight.data)}
                  isLoading={resourceInsight.isLoading}
                />
                <DetailsIdentifier
                  label={'Frameworks'}
                  isLoading={compliance.isLoading}
                  value={renderCompliance(
                    compliance.data?.[alert.alert_rule_id ?? ''] ?? [],
                  )}
                />
                <DetailsIdentifier
                  label={'Generated By'}
                  value={
                    <HStack spacing={0}>
                      {alert.blueprints?.[0] && (
                        <Link to={'#'}>
                          <EllipsisText color={'primary'}>
                            {alert.blueprints?.[0]?.blueprint_name}
                          </EllipsisText>
                        </Link>
                      )}
                      {(alert.blueprints?.length ?? 0) > 1 && (
                        <HStack flexShrink={0}>
                          <Box>,</Box>
                          <CustomTooltip
                            label={formatTooltip({
                              header: 'Blueprints',
                              content: (
                                <Stack>
                                  {map(alert.blueprints, o => (
                                    <Box>{o?.blueprint_name}</Box>
                                  ))}
                                </Stack>
                              ),
                              width: 64,
                            })}
                          >
                            <PlusCountTag
                              count={(alert.blueprints?.length ?? 0) - 1}
                            />
                          </CustomTooltip>
                        </HStack>
                      )}
                    </HStack>
                  }
                />
              </Stack>
            </HStack>
          </Stack>
        </GridItem>
        <GridItem colSpan={1}>
          <Stack flex={1} spacing={4} pl={5}>
            <HStack h={30}>
              <Box fontSize="lg" fontWeight={600}>
                Status
              </Box>
              {alertStatus.isLoading && (
                <Box>
                  <CustomSpinner />
                </Box>
              )}
            </HStack>
            <Stack spacing={3}>
              <DetailsIdentifier
                label={'Alert first seen'}
                value={formatDate(alert?.created_at ?? '-')}
              />
              <DetailsIdentifier
                label={'Ticket'}
                value={
                  ticket?.ticket_id ? (
                    <HStack>
                      <Box>{ticket?.ticket_id}</Box>
                      <Box color={customTheme.colors.gray['250']}>
                        [Created{' '}
                        {formatDate(
                          ticket?.created_at ?? '-',
                          'DD-MMM-YYYY, hh:mm A',
                        )}
                        ]
                      </Box>
                    </HStack>
                  ) : (
                    '-'
                  )
                }
              />
              <DetailsIdentifier
                label={'Due date'}
                value={
                  isEmpty(ticket) ? (
                    '-'
                  ) : (
                    <Box maxW={200}>
                      <CustomDayPicker
                        dateProps={{
                          disabled: { before: new Date() },
                          mode: 'single' as any,
                          onSelect: (selected: any) => {
                            const day = dayjs(selected).format('YYYY-MM-DD');
                            updateAlertDueDate(day);
                          },
                          selected: (ticket?.due_date as String)
                            ? (dayjs(ticket?.due_date!) as any)
                            : undefined,
                        }}
                      />
                    </Box>
                  )
                }
                align="center"
              />
              <DetailsIdentifier
                label={'SLA violation'}
                value={renderSlaViolation(ticket)}
              />
            </Stack>
          </Stack>
        </GridItem>
      </Grid>
      <Divider />
      <Stack pt={2} spacing={5}>
        <Box fontSize="lg" fontWeight={600}>
          Remediation Options
        </Box>
        <AlertRemediation alert={alert} showNote={false} showHeader={false} />
      </Stack>

      {ticketModal.isOpen && (
        <CreateTicket
          onClose={ticketModal.onClose}
          isOpen={ticketModal.isOpen}
          alert={alert}
          onCreate={() => {
            getAlertStatus(alert.uuid!);
          }}
          notification_for={NotificationFor.Alerts}
        />
      )}
      {notificationModal.isOpen && (
        <CreateNotification
          onClose={notificationModal.onClose}
          isOpen={notificationModal.isOpen}
          alert={alert}
          notification_for={NotificationFor.Alerts}
        />
      )}
      {exceptionModal.isOpen && (
        <AddException
          onClose={exceptionModal.onClose}
          isOpen={exceptionModal.isOpen}
          alert={alert}
          exceptionFor="Issue"
        />
      )}
    </Stack>
  );
};
